import { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Autocomplete from '@mui/material/Autocomplete';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import DateTimePicker from '@mui/lab/DateTimePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import client from '../../../client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMessage } from 'app/shared-components/MessageUtils';

const ModalAddFuelTransaction = ({ open, setOpen, tankData, driverData, truckData, onAdded, _fuelnLubeCatalog }) => {
    const {t} = useTranslation('navigation');
  const [kindOfContentId, setKindOfContentId] = useState('');
  const [fuelnLubeCatalog, setFuelnLubeCatalog] = useState([]);
  const [hasSelection, setHasSelection] = useState(false);
  const [tankLocations, setTankLocations] = useState([]);
  const [form, setForm] = useState({
        compacctnum: '',
        container_tank_id: '',
        quantity: '',
        price_per_galon: '',
        mileage: '',
        notes: '',
        kind_of_content: '',
        kind_of_content_id: '',
        driver_id: '',
        truck_id: '',
        transaction_date: null,
    });
  const [validationErrors, setValidationErrors] = useState({});

  const dispatch = useDispatch();
  const showMessageUtil = useMessage();

  useEffect(() => {
    try {
      setTankLocations(tankData || []);
      setFuelnLubeCatalog(_fuelnLubeCatalog || []);
      const lSData = JSON.parse(localStorage.getItem('persist:root'));
      const userData = JSON.parse(lSData.loadz_auth).AuthReducer.user;
      setForm(f => ({ ...f, compacctnum: userData.company.compacctnum }));
    } catch (e) {}
  }, [open]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm({
      container_tank_id: '',
      quantity: '',
      price_per_galon: '',
      mileage: '',
      notes: '',
      kind_of_content: '',
      kind_of_content_id: '',
      driver_id: '',
      truck_id: '',
      transaction_date: null,
    });
    setKindOfContentId('');
    setHasSelection(false);
    setValidationErrors({});
  };

  const validateForm = () => {
    const errors = {};
    
    // All fields are required except notes
    if (!form.kind_of_content) {
      errors.kind_of_content = t('FIELD_REQUIRED');
    }
    if (!form.container_tank_id) {
      errors.container_tank_id = t('FIELD_REQUIRED');
    }
    if (!form.driver_id) {
      errors.driver_id = t('FIELD_REQUIRED');
    }
    if (!form.truck_id) {
      errors.truck_id = t('FIELD_REQUIRED');
    }
    if (!form.quantity || form.quantity.trim() === '') {
      errors.quantity = t('FIELD_REQUIRED');
    }
    if (!form.mileage || form.mileage.trim() === '') {
      errors.mileage = t('FIELD_REQUIRED');
    }
    if (!form.transaction_date) {
      errors.transaction_date = t('FIELD_REQUIRED');
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = () => {
    return form.kind_of_content && 
           form.container_tank_id && 
           form.driver_id && 
           form.truck_id && 
           form.quantity && 
           form.quantity.trim() !== '' && 
           form.mileage && 
           form.mileage.trim() !== '' &&
           form.transaction_date;
  };

  const handleSubmit = async () => {
    // Validate form before submission
    if (!validateForm()) {
      showMessageUtil(t('PLEASE_FILL_ALL_REQUIRED_FIELDS'), 'error');
      return;
    }

    try {
      form.kind_of_content_id = kindOfContentId;
      
      // Validar campos requeridos antes de llamar a la API
      const requiredFields = {
        kind_of_content_id: 'Kind of Content',
        mileage: t('MILEAGE'),
        truck_id: t('TRUCK'),
        driver_id: t('DRIVER'),
        quantity: t('QUANTITY'),
        container_tank_id: t('TANK_IDENTIFIER')
      };

      for (const [field, label] of Object.entries(requiredFields)) {
        if (!form[field] || (typeof form[field] === 'string' && form[field].trim() === '')) {
          showMessageUtil(`${label} ${t('IS_EMPTY_PLEASE_FILL_IT')}`, 'error');
          return;
        }
      }
      
      const response = await client.post('/fuel-inventory/add-fuel-transaction/', form);
      
      // Mostrar mensaje de éxito si la API devuelve un mensaje
      if (response.data && response.data.message) {
        showMessageUtil(response.data.message, 'success');
      } else {
        showMessageUtil(t('FUEL_TRANSACTION_ADDED_SUCCESSFULLY'), 'success');
      }
      
      // Limpiar el formulario después de una llamada exitosa
      resetForm();
      
      if (onAdded) onAdded();
      setOpen(false);
    } catch (err) {
      // Capturar el mensaje de error de la respuesta de la API
      let errorMessage = t('ERROR_ADDING_FUEL_TRANSACTION');
      
      if (err.response && err.response.data) {
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
          showMessageUtil(errorMessage, 'error');
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
          showMessageUtil(errorMessage, 'error');
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
          showMessageUtil(errorMessage, 'error');
        }
      } else if (err.message) {
        errorMessage = err.message;
        showMessageUtil(errorMessage, 'error');
      }
      
      showMessageUtil(errorMessage, 'error');
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>{t('ADD_TRANSACTION')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Row 1: Kind of Content + Transaction Date */}
          <Grid item xs={12} sm={6}>
            <label>
              {t('KIND_OF_TRANSACTION')}
            </label>
            <FormControl fullWidth margin="normal" error={!!validationErrors.kind_of_content}>
              <InputLabel>{t('TANK_CONTENTS')}</InputLabel>
              <Select
                name="kind_of_content"
                style={{ paddingTop: '8.5px', paddingBottom: '8.5px' }}
                value={form.kind_of_content}
                onChange={(e) => {
                  setForm({ ...form, kind_of_content: e.target.value });
                  setKindOfContentId(_fuelnLubeCatalog.find(fuel => fuel.product_name === e.target.value)?.product_id);
                  setHasSelection(!!e.target.value);
                  setTankLocations(tankData.filter(tank => parseInt(tank.kind_of_content) === _fuelnLubeCatalog.find(fuel => fuel.product_name === e.target.value)?.product_id));
                  // Clear validation error when field is filled
                  if (validationErrors.kind_of_content) {
                    setValidationErrors({ ...validationErrors, kind_of_content: '' });
                  }
                }}
                label={t('TANK_CONTENTS')}
                SelectDisplayProps={{ style: { padding: '5px' } }}
                required
              >
                {fuelnLubeCatalog.map(fuel => (
                  <MenuItem key={fuel.id} value={fuel.product_name}>
                    {fuel.product_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <label>Transaction Date</label>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Transaction Date"
                inputProps={{
                  style: {
                    paddingTop: '7.5px',
                    paddingBottom: '7.5px',
                  },
                }}
                disabled={!hasSelection}
                value={form.transaction_date}
                onChange={(newValue) => {
                  setForm({ ...form, transaction_date: newValue });
                  // Clear validation error when field is filled
                  if (validationErrors.transaction_date) {
                    setValidationErrors({ ...validationErrors, transaction_date: '' });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    required
                    error={!!validationErrors.transaction_date}
                    helperText={validationErrors.transaction_date}
                    variant="outlined"
                    placeholder="Select date and time"
                  />
                )}
                inputFormat="MM/dd/yyyy HH:mm"
                ampm={false}
              />
            </LocalizationProvider>
          </Grid>

          {/* Row 2: Tank + Driver */}
          <Grid item xs={12} sm={6}>
            <label>{t('TANK_IDENTIFIER')}</label>
            <FormControl fullWidth margin="normal">
              <Autocomplete
                disabled={!hasSelection}
                options={tankLocations}
                getOptionLabel={option => option.location ? option.location : ''}
                value={tankData.find(tank => tank.id === form.container_tank_id) || null}
                onChange={(event, newValue) => {
                  setForm({ ...form, container_tank_id: newValue ? newValue.id : '' });
                  // Clear validation error when field is filled
                  if (validationErrors.container_tank_id) {
                    setValidationErrors({ ...validationErrors, container_tank_id: '' });
                  }
                }}
                renderInput={params => (
                  <TextField 
                    {...params} 
                    variant="outlined" 
                    fullWidth 
                    required
                    error={!!validationErrors.container_tank_id}
                    helperText={validationErrors.container_tank_id}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <label>{t('DRIVER')}</label>
            <FormControl fullWidth margin="normal">
                <Autocomplete
                disabled={!hasSelection}
                options={driverData}
                  getOptionLabel={option => option.user_fname && option.user_lname ? `${option.user_fname} ${option.user_lname}` : ''}
                  value={driverData.find(driver => driver.dr_user_id === form.driver_id) || null}
                  onChange={(event, newValue) => {
                    setForm({ ...form, driver_id: newValue ? newValue.dr_user_id : '' });
                    // Clear validation error when field is filled
                    if (validationErrors.driver_id) {
                      setValidationErrors({ ...validationErrors, driver_id: '' });
                    }
                  }}
                  renderInput={params => (
                    <TextField 
                      {...params} 
                      variant="outlined" 
                      fullWidth 
                      required
                      error={!!validationErrors.driver_id}
                      helperText={validationErrors.driver_id}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.dr_user_id === value.dr_user_id}
                />
            </FormControl>
          </Grid>

          {/* Row 3: Truck + Quantity */}
          <Grid item xs={12} sm={6}>
            <label>{t('TRUCK')}</label>
            <FormControl fullWidth margin="normal">
                <Autocomplete
                disabled={!hasSelection}
                options={truckData}
                  getOptionLabel={option => option.t_no_truck ? `No. ${option.t_no_truck}` : ''}
                  value={truckData.find(truck => truck.idx_trucks_p === form.truck_id) || null}
                  onChange={(event, newValue) => {
                    setForm({ ...form, truck_id: newValue ? newValue.idx_trucks_p : '' });
                    // Clear validation error when field is filled
                    if (validationErrors.truck_id) {
                      setValidationErrors({ ...validationErrors, truck_id: '' });
                    }
                  }}
                  renderInput={params => (
                    <TextField 
                      {...params} 
                      variant="outlined" 
                      fullWidth 
                      required
                      error={!!validationErrors.truck_id}
                      helperText={validationErrors.truck_id}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.idx_trucks_p === value.idx_trucks_p}
                />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <label>{t('QUANTITY')}</label>
            <TextField
              disabled={!hasSelection}
              inputProps={{
                style: {
                  paddingTop: '7.5px',
                  paddingBottom: '7.5px',
                },
              }}
              margin="normal"
              fullWidth
              name="quantity"
              value={form.quantity}
              onChange={(e) => {
                handleChange(e);
                // Clear validation error when field is filled
                if (validationErrors.quantity) {
                  setValidationErrors({ ...validationErrors, quantity: '' });
                }
              }}
              required
              error={!!validationErrors.quantity}
              helperText={validationErrors.quantity}
            />
          </Grid>

          {/* Row 4: Mileage + Notes */}
          <Grid item xs={12} sm={6}>
            <label>{t('MILEAGE')}</label>
            <TextField
              disabled={!hasSelection}
              inputProps={{
                style: {
                  paddingTop: '7.5px',
                  paddingBottom: '7.5px',
                },
              }}
              margin="normal"
              fullWidth
              name="mileage"
              value={form.mileage}
              onChange={(e) => {
                handleChange(e);
                // Clear validation error when field is filled
                if (validationErrors.mileage) {
                  setValidationErrors({ ...validationErrors, mileage: '' });
                }
              }}
              type="number"
              required
              error={!!validationErrors.mileage}
              helperText={validationErrors.mileage}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <label>{t('NOTES')}</label>
            <TextField
              disabled={!hasSelection}
              inputProps={{
                style: {
                  paddingTop: '7.5px',
                  paddingBottom: '7.5px',
                },
              }}
              margin="normal"
              fullWidth
              name="notes"
              value={form.notes}
              onChange={handleChange}
              placeholder={t('OPTIONAL')}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>{t('CANCEL')}</Button>
        <Button disabled={!hasSelection || !isFormValid()} onClick={handleSubmit} variant="contained">{t('ADD')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalAddFuelTransaction;
